import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, AlertCircle, Check, ArrowLeft } from 'lucide-react';
import { forgotPassword } from '../api/client';

export function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await forgotPassword(email);
      setSubmitted(true);
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setError(detail || 'Could not send reset email. Please try again later.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-4 py-12">
      <Link
        to="/login"
        className="inline-flex items-center text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 mb-6"
      >
        <ArrowLeft className="w-4 h-4 mr-1" />
        Back to sign in
      </Link>

      <div className="card p-6">
        <h1 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Reset your password</h1>

        {submitted ? (
          <div className="space-y-3">
            <div className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg flex items-start gap-2 text-sm text-green-700 dark:text-green-300">
              <Check className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>
                If an account exists for <strong>{email}</strong>, we've sent it a link to reset
                the password. Please check your inbox.
              </span>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              The link expires in one hour. Check your spam folder if you don't see it.
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4" autoComplete="off">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Enter the email address associated with your account and we'll send you a reset
              link.
            </p>
            {error && (
              <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center gap-2 text-sm text-red-700 dark:text-red-400">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Email
              </label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-input text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-orange focus:border-transparent"
                placeholder="you@example.com"
              />
            </div>
            <button
              type="submit"
              disabled={submitting || !email}
              className="w-full px-4 py-2 text-sm font-medium text-white bg-brand-orange hover:bg-brand-orange-dark rounded-full disabled:opacity-50"
            >
              {submitting ? (
                <Loader2 className="w-4 h-4 animate-spin mx-auto" />
              ) : (
                'Send reset link'
              )}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
